import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";

/* ==========================================================================
 * Shared analytics widgets — KPI tile + chart wrappers with consistent styling.
 * ==========================================================================*/

export const COLORS = ["#1FB6C1", "#A78BFA", "#F59E0B", "#10B981", "#F43F5E", "#06B6D4", "#EAB308", "#6366F1", "#FB923C", "#EC4899"];

const TOOLTIP_STYLE = {
  backgroundColor: "#121419",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "6px",
  fontSize: "12px",
};

export function Kpi({ label, value, unit = "", accent = "#1FB6C1", testId, note, big = false }) {
  return (
    <div className="hv-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className={`font-display font-bold mt-1 ${big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`} style={{ color: accent }}>
        {value}
        {unit && <span className="text-base md:text-lg ml-1 text-[#a0aabf]">{unit}</span>}
      </div>
      {note && <div className="text-xs text-[#a0aabf] mt-1">{note}</div>}
    </div>
  );
}

export function ChartCard({ title, desc, height = 240, children, testId }) {
  return (
    <div className="hv-card p-4" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h3 className="font-display font-bold text-base md:text-lg">{title}</h3>
        {desc && <div className="text-xs text-[#647087]">{desc}</div>}
      </div>
      <div style={{ width: "100%", height }}>
        {children}
      </div>
    </div>
  );
}

export function Empty({ label = "No data yet" }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-xs text-[#647087] italic">
      {label}
    </div>
  );
}

export function Bars({ data, xKey, yKey, color = "#1FB6C1", height = 240 }) {
  if (!data || data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey={xKey} stroke="#647087" style={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
        <YAxis stroke="#647087" style={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Bar dataKey={yKey} fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, nameKey, valueKey, height = 240 }) {
  if (!data || data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="90%"
          paddingAngle={2}
        >
          {data.map((_, idx) => (
            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Legend wrapperStyle={{ fontSize: 11, color: "#a0aabf" }} iconType="circle" />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function LineTrend({ data, xKey, yKey, color = "#1FB6C1", height = 200 }) {
  if (!data || data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey={xKey} stroke="#647087" style={{ fontSize: 10 }} />
        <YAxis stroke="#647087" style={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Line type="monotone" dataKey={yKey} stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function HBar({ data, labelKey, valueKey, color = "#1FB6C1", height = 220, testId }) {
  // Horizontal bar — good for leaderboards / long category names.
  if (!data || data.length === 0) return <Empty />;
  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div className="space-y-1.5" style={{ height, overflow: "auto" }} data-testid={testId}>
      {data.map((row, i) => {
        const pct = ((row[valueKey] || 0) / max) * 100;
        return (
          <div key={i} className="text-xs">
            <div className="flex justify-between items-baseline mb-0.5">
              <span className="text-[#dde3ec] truncate max-w-[70%]">{row[labelKey]}</span>
              <span className="font-mono text-[#a0aabf]">{row[valueKey]}</span>
            </div>
            <div className="h-2 rounded bg-white/5 overflow-hidden">
              <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function fmtZAR(n) {
  if (n === null || n === undefined) return "—";
  return `R ${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}
